/*
Auto-generated by: https://github.com/pmndrs/gltfjsx
*/

import { animated, useSpringValue } from '@react-spring/three'
import { useGLTF } from '@react-three/drei'
import type { ObjectMap } from '@react-three/fiber'
import { type JSX, useEffect, useMemo } from 'react'
import { IcosahedronGeometry, MathUtils, type Mesh, MeshStandardMaterial, Object3D } from 'three'
import type { GLTF } from 'three-stdlib'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'

import { toLambert } from '@utils'

const ICOSAHEDRON_GEOMETRY = new IcosahedronGeometry(0.044, 2)

type GLTFResult = GLTF &
  ObjectMap & {
    nodes: {
      mesh106280964002: Mesh
      mesh106280964002_1: Mesh
      mesh106280964002_2: Mesh
      mesh106280964002_3: Mesh
      mesh106280964002_4: Mesh
      mesh411581995001: Mesh
      mesh411581995001_1: Mesh
      mesh411581995001_2: Mesh
      mesh411581995001_3: Mesh
      group106280964001: Mesh
      group106280964002: Mesh
    }
    materials: {
      ['mat17.001']: MeshStandardMaterial
      ['mat15.001']: MeshStandardMaterial
      ['mat23.001']: MeshStandardMaterial
      ['mat8.001']: MeshStandardMaterial
      ['mat16.001']: MeshStandardMaterial
      ['mat21.001']: MeshStandardMaterial
    }
  }

type RailroadCrossingModelProps = JSX.IntrinsicElements['group'] & {
  open?: boolean
}

export function RailroadCrossingModel({ open, ...props }: RailroadCrossingModelProps) {
  const { nodes, materials: stdMaterials } = useGLTF('/models/railroad-crossing.glb') as GLTFResult

  const materials = useMemo(
    () => ({
      'mat17.001': toLambert(stdMaterials['mat17.001']),
      'mat15.001': toLambert(stdMaterials['mat15.001']),
      'mat23.001': toLambert(stdMaterials['mat23.001']),
      'mat8.001': toLambert(stdMaterials['mat8.001']),
      'mat16.001': toLambert(stdMaterials['mat16.001']),
      'mat21.001': toLambert(stdMaterials['mat21.001']),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  useEffect(() => {
    return () => Object.values(materials).forEach(m => m.dispose())
  }, [materials])

  const lightIntensity = useSpringValue(0)
  const emissiveIntensity = useSpringValue(0)
  const rotation = useSpringValue(0)

  const intensityMax = 1
  const emissiveMax = 10
  const inverseEmissiveIntensity = useMemo(
    () => emissiveIntensity.to(v => emissiveMax - v),
    [emissiveIntensity],
  )
  const inverseLightIntensity = useMemo(
    () => lightIntensity.to(v => intensityMax - v),
    [lightIntensity],
  )

  useEffect(() => {
    lightIntensity.start(intensityMax, {
      config: { duration: 50 },
      loop: { reverse: true, delay: 500 },
    })
    emissiveIntensity.start(emissiveMax, {
      config: { duration: 50 },
      loop: { reverse: true, delay: 500 },
    })

    return () => {
      lightIntensity.stop()
      emissiveIntensity.stop()
      rotation.stop()
    }
  }, [lightIntensity, emissiveIntensity, rotation])

  useEffect(() => {
    rotation.start(MathUtils.degToRad(open ? 85 : 0), { config: { tension: 80, friction: 14 } })
  }, [open, rotation])

  const { barGeometry, barMaterials } = useMemo(() => {
    const inner = new Object3D()
    inner.position.set(-0.1, 0.2, 0)

    const barBaseObj = new Object3D()
    barBaseObj.position.set(0.05, 0, 0.025)
    barBaseObj.scale.set(0.8, 1.1, 1.1)
    inner.add(barBaseObj)

    const barObj = new Object3D()
    barObj.position.set(0, -0.005, 0.05)
    inner.add(barObj)

    inner.updateWorldMatrix(false, true)

    const parts = [
      {
        geo: nodes.mesh411581995001.geometry.clone().applyMatrix4(barBaseObj.matrixWorld),
        mat: materials['mat17.001'],
      },
      {
        geo: nodes.mesh411581995001_1.geometry.clone().applyMatrix4(barBaseObj.matrixWorld),
        mat: materials['mat15.001'],
      },
      {
        geo: nodes.mesh411581995001_2.geometry.clone().applyMatrix4(barObj.matrixWorld),
        mat: materials['mat21.001'],
      },
      {
        geo: nodes.mesh411581995001_3.geometry.clone().applyMatrix4(barObj.matrixWorld),
        mat: materials['mat8.001'],
      },
    ]

    const barGeometry = mergeGeometries(
      parts.map(p => p.geo),
      true,
    )
    parts.forEach(p => p.geo.dispose())
    return { barGeometry, barMaterials: parts.map(p => p.mat) }
  }, [nodes, materials])

  const { staticGeometry, staticMaterials } = useMemo(() => {
    const obj1 = new Object3D()
    obj1.position.set(-0.032, 0.608, 0)
    obj1.rotation.set(0, 0.037, 0)
    obj1.updateMatrix()

    const obj2 = new Object3D()
    obj2.position.set(-0.033, 0.398, -0.04)
    obj2.updateMatrix()

    const clonedGeo1 = nodes.group106280964001.geometry.clone().applyMatrix4(obj1.matrix)
    const clonedGeo2 = nodes.group106280964002.geometry.clone().applyMatrix4(obj2.matrix)

    const parts = [
      { geo: nodes.mesh106280964002.geometry, mat: materials['mat17.001'] },
      { geo: nodes.mesh106280964002_1.geometry, mat: materials['mat15.001'] },
      { geo: nodes.mesh106280964002_2.geometry, mat: materials['mat23.001'] },
      { geo: nodes.mesh106280964002_4.geometry, mat: materials['mat16.001'] },
      { geo: clonedGeo1, mat: materials['mat15.001'] },
      { geo: clonedGeo2, mat: materials['mat21.001'] },
    ]

    const staticGeometry = mergeGeometries(
      parts.map(p => p.geo),
      true,
    )
    clonedGeo1.dispose()
    clonedGeo2.dispose()
    return { staticGeometry, staticMaterials: parts.map(p => p.mat) }
  }, [nodes, materials])

  useEffect(() => {
    return () => {
      barGeometry?.dispose()
    }
  }, [barGeometry])

  useEffect(() => {
    return () => {
      staticGeometry?.dispose()
    }
  }, [staticGeometry])

  return (
    <group {...props}>
      <group position-y={0.48} scale={[1.2, 1.2, 1.2]}>
        <mesh castShadow receiveShadow geometry={staticGeometry} material={staticMaterials} />

        <mesh material={materials['mat8.001']}>
          <mesh
            position={[-0.135, 0.074, 0.01]}
            scale={[0.9, 0.9, 2]}
            geometry={ICOSAHEDRON_GEOMETRY}
          >
            <animated.meshLambertMaterial
              color={open ? 'limegreen' : 'red'}
              emissive={open ? 'limegreen' : 'red'}
              emissiveIntensity={emissiveIntensity}
              toneMapped={false}
            />
            <animated.pointLight color={open ? 'limegreen' : 'red'} intensity={lightIntensity} />
          </mesh>
          <mesh
            position={[0.075, 0.078, 0.005]}
            scale={[0.9, 0.9, 2]}
            geometry={ICOSAHEDRON_GEOMETRY}
          >
            <animated.meshLambertMaterial
              color={open ? 'limegreen' : 'red'}
              emissive={open ? 'limegreen' : 'red'}
              emissiveIntensity={inverseEmissiveIntensity}
              toneMapped={false}
            />
            <animated.pointLight
              color={open ? 'limegreen' : 'red'}
              intensity={inverseLightIntensity}
            />
          </mesh>
        </mesh>

        <animated.group
          position-y={-0.15}
          rotation-y={MathUtils.degToRad(1.1)}
          rotation-z={rotation}
        >
          <mesh castShadow receiveShadow geometry={barGeometry} material={barMaterials} />
        </animated.group>
      </group>
    </group>
  )
}

useGLTF.preload('/models/railroad-crossing.glb')
